import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { DetailedCitizenAcsVisitsIndicatorC6Data } from "@/services/esus/indicator/esf/c6/getDetailedCitizensAcsVisitsIndicatorC6";
import { getCitizenScoreStatusDescriptionIndicatorC6 } from "@/utils/indicatorsEsf";

import { IndicatorEsfC6CitizensAcsVisitsFilters } from "./IndicatorEsfC6CitizensAcsVisitsFilters";
import { IndicatorEsfC6CitizensAcsVisitsTable } from "./IndicatorEsfC6CitizensAcsVisitsTable";
import { IndicatorEsfC6CitizensAcsVisitsHistory } from "./IndicatorEsfC6CitizensAcsVisitsHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC6CitizensAcsVisitsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedCitizensAcsVisits: Paginated<DetailedCitizenAcsVisitsIndicatorC6Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllDetailedCitizensAcsVisitsIndicatorC6: () => Promise<
		DetailedCitizenAcsVisitsIndicatorC6Data[]
	>;
};

export function IndicatorEsfC6CitizensAcsVisits({
	selectedQuarter,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedCitizensAcsVisits,
	pageNumber,
	setPageNumber,
	handleGetAllDetailedCitizensAcsVisitsIndicatorC6: handleGetAllDetailedIndicatorC1,
}: Readonly<IndicatorEsfC6CitizensAcsVisitsProps>) {
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithAcsVisits, setCitizenWithAcsVisits] =
		useState<DetailedCitizenAcsVisitsIndicatorC6Data>(
			new DetailedCitizenAcsVisitsIndicatorC6Data()
		);
	const [isLoadingCitizenWithAcsHomeVisits, setIsLoadingCitizenWithAcsHomeVisits] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAcsVisits(
		citizenWithHomeVisits: DetailedCitizenAcsVisitsIndicatorC6Data
	) {
		setIsLoadingCitizenWithAcsHomeVisits(true);
		setCitizenWithAcsVisits(citizenWithHomeVisits);
		setIsLoadingCitizenWithAcsHomeVisits(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllDetailedIndicatorC1();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformCitizensWithAcsVisitsToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Visitas ACS - ${dayjs(quarterDates.startDate).format("DD-MM-YYYY")} à ${dayjs(
					quarterDates.endDate
				).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformCitizensWithAcsVisitsToGenericObjectArray(
		citizensWithAcsVisits: DetailedCitizenAcsVisitsIndicatorC6Data[]
	) {
		return citizensWithAcsVisits.map(
			(citizenWithAcsVisits) =>
				({
					Nome: citizenWithAcsVisits.fullName,
					DN: dayjs(citizenWithAcsVisits.birthdate).format("DD/MM/YYYY"),
					Idade: getAge(citizenWithAcsVisits.birthdate),
					Documento: citizenWithAcsVisits.cpf ?? citizenWithAcsVisits.cns ?? "",
					Area: citizenWithAcsVisits.area ?? "FA",
					"Ine Equipe": citizenWithAcsVisits.healthTeam?.ine ?? "",
					"Nome Equipe": citizenWithAcsVisits.healthTeam?.name ?? "",
					"1ª Visita": citizenWithAcsVisits.firstAcsHomeVisit?.date
						? dayjs(citizenWithAcsVisits.firstAcsHomeVisit.date).format("DD/MM/YYYY")
						: "",
					"Status 1ª Visita": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenWithAcsVisits.firstAcsHomeVisitStatus
					),
					"Última Visita": citizenWithAcsVisits.lastAcsHomeVisit?.date
						? dayjs(citizenWithAcsVisits.lastAcsHomeVisit.date).format("DD/MM/YYYY")
						: "",
					"Status Última Visita": getCitizenScoreStatusDescriptionIndicatorC6(
						citizenWithAcsVisits.twoHomeVisitsStatus
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingCitizenWithAcsHomeVisits || isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC6CitizensAcsVisitsFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC6CitizensAcsVisitsTable
							citizensAcsVisits={paginatedCitizensAcsVisits.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAcsVisits}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedCitizensAcsVisits?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedCitizensAcsVisits.pageSize}
								totalPages={paginatedCitizensAcsVisits.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Visitas dos ACS
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsfC6CitizensAcsVisitsHistory
							citizenWithAcsHomeVisits={citizenWithAcsVisits}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
